import React, { useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import {
  getAuth,
  signOut,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
  updateEmail,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";

interface AccountProfileModalProps {
  auth: any;
  db: any;
  currentUser: any;
  updateCurrentUser: any;
}

const TitleList: { [key: string]: string } = {
  DR: "Dr",
  MR: "Mr",
  MRS: "Mrs",
  MS: "Ms",
  MISS: "Miss",
};

const SexList: { [key: string]: string } = {
  FEMALE: "Female",
  MALE: "Male",
  OTHER: "Other",
};

const PhoneTypeList: { [key: string]: string } = {
  MOBILE: "Mobile",
  HOME: "Home",
  WORK: "Work",
  FAX: "Fax",
  OTHER: "Other",
};

const OccupationList: { [key: string]: string } = {
  ADMINISTRATIVE: "Administrative",
  CHIROPRACTOR: "Chiropractor",
  CLINIC_MANAGER: "Clinic Manager",
  DOCTOR: "Doctor",
  EXERCISE_SPECIALIST: "Exercise Specialist",
  NURSE: "Nurse",
  OCCUPATIONAL_THERAPIST: "Occupational Therapist",
  OSTEOPATH: "Osteopath",
  PHYSIOTHERAPIST: "Physiotherapist",
  PODIATRIST: "Podiatrist",
  PSYCHOLOGIST: "Psychologist",
  STUDENT: "Student",
  SURGEON: "Surgeon",
  OTHER: "Other",
};

const AccountProfileModal = ({
  auth,
  db,
  currentUser,
  updateCurrentUser,
}: AccountProfileModalProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMismatchError, setPasswordMismatchError] = useState("");
  const [title, setTitle] = useState(currentUser.title || "");
  const [firstName, setFirstName] = useState(currentUser.firstName || "");
  const [lastName, setLastName] = useState(currentUser.lastName || "");
  const [dob, setDob] = useState(currentUser.dob || "");
  const [occupation, setOccupation] = useState(
    currentUser.occupation || "ADMINISTRATIVE"
  );
  const [speciality, setSpeciality] = useState(currentUser.speciality || "");
  const [sex, setSex] = useState(currentUser.sex || "FEMALE");
  const [phone, setPhone] = useState(currentUser.phone || "");
  const [phoneType, setPhoneType] = useState(currentUser.phoneType || "MOBILE");
  const [newEmail, setNewEmail] = useState(currentUser.email || "");
  const [emailChangeInitiated, setEmailChangeInitiated] = useState(false);

  const inputStyle: React.CSSProperties = {
    boxSizing: "border-box",
    margin: "0 0 1rem 0",
    padding: "0.5rem",
    border: "1px solid #ccc",
    borderRadius: "4px",
    width: "100%",
  };

  function formatDateToDDMMYYYY(isoString: string) {
    if (!isoString) return "";
    const date = new Date(isoString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0"); // getMonth() is zero-based
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  const handleEditClick = () => setIsEditing(true);

  const handleSaveClick = async () => {
    try {
      const userDocRef = doc(db, "users", currentUser.id);
      await updateDoc(userDocRef, {
        title,
        firstName,
        lastName,
        dob,
        occupation,
        speciality,
        sex,
        phone,
        phoneType,
        // Add additional fields as necessary
      });

      if (auth.currentUser.email !== newEmail) {
        const userConfirmed = window.confirm(
          "Are you sure you want to change your email?"
        );

        if (userConfirmed) {
          await updateEmail(auth.currentUser, newEmail);
          setEmailChangeInitiated(true);
          await sendEmailVerification(newEmail);
        } else {
          setNewEmail(currentUser.email);
        }
      }
      const updatedUser = { ...currentUser, title, firstName, lastName, dob, occupation, speciality, sex, phone, phoneType };
      updateCurrentUser(updatedUser); // Pass the updated currentUser to the callback function
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile information:", error);
    }
  };

  const handleChangePassword = () => {
    setIsChangingPassword(true);
    if (newPassword !== confirmPassword) {
      setPasswordMismatchError("New passwords do not match.");
      setIsChangingPassword(false);
      return;
    }

    if (!newPassword) {
      alert("New password cannot be empty.");
      setIsChangingPassword(false);
      return;
    }

    const user = auth.currentUser;
    const email = user && user.email ? user.email : "";
    const credential = EmailAuthProvider.credential(email, currentPassword);

    // Reauthenticate the user
    if (user) {
      reauthenticateWithCredential(user, credential)
        .then(() => {
          // User re-authenticated, now change the password
          updatePassword(user, newPassword)
            .then(() => {
              // setSuccess(true);
              // setError("");
              alert("Password changed successfully!");
              setShowChangePassword(false);
              // Reset password fields and isChangingPassword state
              setCurrentPassword("");
              setNewPassword("");
              setConfirmPassword("");
              setIsChangingPassword(false);
            })
            .catch((error): void => {
              let errorMessage = "Error changing password: " + error.message;
              switch (error.code) {
                case "auth/wrong-password":
                  errorMessage = "The password is invalid.";
                  break;
                case "auth/weak-password":
                  errorMessage =
                    "The password is too short. Please choose a different password.";
                  break;
                case "auth/missing-password":
                  errorMessage = "Please enter your password.";
                  break;
                case "auth/invalid-email":
                  errorMessage = "Please enter a valid email address.";
                  break;
                case "auth/user-not-found":
                  errorMessage = "No user found with this email.";
                  break;
                case "auth/too-many-requests":
                  errorMessage =
                    "Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later.";
                  break;
              }
              alert(errorMessage);
              setIsChangingPassword(false);
            });
        })
        .catch((error) => {
          // setError("Current password is incorrect.");
          alert("Current password is incorrect.");
          setIsChangingPassword(false);
        });
    }
  };

  function renderChangePassword() {
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleChangePassword();
    };

    return (
      <div className="box-sizing-default account-modal-content">
        <h2>Change Password</h2>
        <form
          className="box-sizing-default change-password-form"
          onSubmit={handleSubmit}
        >
          <input
            className="box-sizing-default"
            style={{ marginRight: "0", width: "100%" }}
            type="password"
            placeholder="Current Password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          <input
            className="box-sizing-default"
            style={{ marginRight: "0", width: "100%" }}
            type="password"
            placeholder="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <input
            className="box-sizing-default"
            style={{ marginRight: "0", width: "100%" }}
            type="password"
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <button
            className="button-small-brand-green"
            style={{ fontSize: "1rem", width: "100%" }}
            type="submit"
            disabled={isChangingPassword}
          >
            Submit
          </button>
          <button
            className="button-link"
            onClick={() => {
              setShowChangePassword(false);
              setCurrentPassword("");
              setNewPassword("");
              setConfirmPassword("");
              setPasswordMismatchError("");
            }}
          >
            Back
          </button>
          {passwordMismatchError && (
            <div className="box-sizing-default alert-text">
              {passwordMismatchError}
            </div>
          )}
        </form>
      </div>
    );
  }

  return (
    <div>
      {showChangePassword ? (
        renderChangePassword()
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            {isEditing ? (
              <button className="button-copy" onClick={handleSaveClick}>
                Save
              </button>
            ) : (
              <button className="button-copy" onClick={handleEditClick}>
                Edit
              </button>
            )}
          </div>
          <div style={{ marginBottom: "0.5rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "bold",
              }}
            >
              Title
            </label>
            {isEditing ? (
              <select
                style={inputStyle}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              >
                <option value="">Select a title</option>
                {Object.entries(TitleList).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            ) : (
              <div style={{ marginBottom: "1.5rem" }}>
                {TitleList[title] || "Not Provided"}
              </div>
            )}
          </div>
          <div style={{ marginBottom: "0.5rem", display: "flex", gap: "1rem" }}>
            <div style={{ flexGrow: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "bold",
                }}
              >
                First Name
              </label>
              {isEditing ? (
                <input
                  style={inputStyle}
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name (e.g., Bob)"
                />
              ) : (
                <div style={{ marginBottom: "1.5rem" }}>
                  {firstName || "Not Provided"}
                </div>
              )}
            </div>
            <div style={{ flexGrow: 1 }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "bold",
                }}
              >
                Last Name
              </label>
              {isEditing ? (
                <input
                  style={inputStyle}
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last Name (e.g., Smith)"
                />
              ) : (
                <div style={{ marginBottom: "1.5rem" }}>
                  {lastName || "Not Provided"}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginBottom: "0.5rem", display: "flex", gap: "1rem" }}>
            <div style={{ width: "100%" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "bold",
                }}
              >
                Date of Birth
              </label>
              {isEditing ? (
                <input
                  style={inputStyle}
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  placeholder="First Name (e.g., Bob)"
                />
              ) : (
                <div style={{ marginBottom: "1.5rem" }}>
                  {formatDateToDDMMYYYY(dob) || "Not Provided"}
                </div>
              )}
            </div>
            <div style={{ width: "100%" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "bold",
                }}
              >
                Sex
              </label>
              {isEditing ? (
                <select
                  style={inputStyle}
                  value={sex}
                  onChange={(e) => setSex(e.target.value)}
                >
                  {Object.entries(SexList).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ marginBottom: "1.5rem" }}>
                  {SexList[sex] || "Not Provided"}
                </div>
              )}
            </div>
          </div>
          <div style={{ marginBottom: "0.5rem", display: "flex", gap: "1rem" }}>
            {/* Occupation */}
            <div style={{ width: "100%" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "bold",
                }}
              >
                Occupation
              </label>
              {isEditing ? (
                <select
                  style={inputStyle}
                  value={occupation}
                  onChange={(e) => setOccupation(e.target.value)}
                >
                  {Object.entries(OccupationList).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ marginBottom: "1.5rem" }}>
                  {OccupationList[occupation] || "Not Provided"}
                </div>
              )}
            </div>
            {/* Speciality */}
            <div style={{ width: "100%" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "bold",
                }}
              >
                Speciality
              </label>
              {isEditing ? (
                <input
                  style={inputStyle}
                  type="text"
                  value={speciality}
                  onChange={(e) => setSpeciality(e.target.value)}
                  placeholder="Speciality (e.g., Pediatrics)"
                />
              ) : (
                <div style={{ marginBottom: "1.5rem" }}>
                  {speciality || "Not Provided"}
                </div>
              )}
            </div>
          </div>
          <div style={{ marginBottom: "0.5rem", display: "flex", gap: "1rem" }}>
            <div style={{ width: "100%" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "bold",
                }}
              >
                Phone
              </label>
              {isEditing ? (
                <input
                  style={inputStyle}
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+442111222333"
                />
              ) : (
                <div style={{ marginBottom: "1.5rem" }}>
                  {phone || "Not Provided"}
                </div>
              )}
            </div>
            <div style={{ width: "100%" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  fontWeight: "bold",
                }}
              >
                Phone Type
              </label>
              {isEditing ? (
                <select
                  style={inputStyle}
                  value={phoneType}
                  onChange={(e) => setPhoneType(e.target.value)}
                >
                  {Object.entries(PhoneTypeList).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              ) : (
                <div style={{ marginBottom: "1.5rem" }}>
                  {PhoneTypeList[phoneType] || "Not Provided"}
                </div>
              )}
            </div>
          </div>

          {/* Email Field */}
          <div style={{ marginBottom: "0.5rem" }}>
            <label
              style={{
                display: "block",
                marginBottom: "0.5rem",
                fontWeight: "bold",
              }}
            >
              Email
            </label>
            <div>{currentUser.email || "Not Provided"}</div>
            {isEditing && (
              <div className="alert-text">
                Please contact us to change your email.
              </div>
            )}
            {/* {isEditing ? (
              <input
                style={inputStyle}
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Email Address"
              />
            ) : (
              <div>{currentUser.email || "Not Provided"}</div>
            )}
            {emailChangeInitiated && (
              <div className="alert-text">
                Email change initiated. Please verify your new email.
              </div>
            )} */}
          </div>

          {!isEditing && (
            <button
              className="button-small-brand-green"
              style={{
                marginTop: "1rem",
                fontSize: "1rem",
              }}
              onClick={() => {
                setShowChangePassword(!showChangePassword);
              }}
            >
              Change Password
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default AccountProfileModal;
